import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ujmsNoticesConfig, ujmsNotices, ujmsResendNotice, ujmsTestNotice, ujmsNoticeSweep, openGmailCompose,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field, fmtDT, cls } from "../ui";
import { Mail, Send, RefreshCw, BellRing } from "lucide-react";

export function NoticesTab() {
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [testEmail, setTestEmail] = useState("");

  const load = useCallback(() => {
    ujmsNoticesConfig().then((r) => setCfg(r.data)).catch(() => {});
    ujmsNotices(status ? { status } : {}).then((r) => setRows(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const resend = async (n) => {
    try {
      const r = await ujmsResendNotice(n.id);
      r.data.status === "FAILED" ? toast.error(`Send failed — ${r.data.error}`) : toast.success(`Notice ${r.data.status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const test = async () => {
    try {
      const r = await ujmsTestNotice(testEmail);
      r.data.status === "FAILED" ? toast.error(`Test failed — ${r.data.error}`) :
        toast.success(r.data.mode === "LIVE" ? `Test email SENT to ${testEmail}` : "Recorded in SIMULATED mode (no key configured)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };
  const sweep = async () => {
    try { const r = await ujmsNoticeSweep(); toast.success(`${r.data.queued} hearing reminder(s) queued`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sweep failed"); }
  };

  return (
    <div className="space-y-4" data-testid="notices-tab">
      {cfg && (
        <div className={cls("border rounded-sm px-4 py-3 flex flex-wrap items-center gap-3",
          cfg.mode === "LIVE" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300")}
          data-testid="notices-mode-banner">
          <Mail className={cls("w-5 h-5", cfg.mode === "LIVE" ? "text-emerald-700" : "text-amber-700")} />
          <div className="flex-1 min-w-[260px]">
            <div className="text-sm font-semibold text-slate-900">
              Notice delivery: {cfg.mode === "LIVE" ? "LIVE (Resend)" : "SIMULATED"}
              <span className="font-normal text-slate-500"> · sender {cfg.sender}</span>
            </div>
            {cfg.hint && <div className="text-xs text-amber-800 mt-0.5">{cfg.hint}</div>}
          </div>
          <div className="flex gap-3 font-mono text-xs text-slate-600" data-testid="notices-counts">
            <span className="text-emerald-700">{cfg.counts.SENT} sent</span>
            <span className="text-amber-700">{cfg.counts.SIMULATED} simulated</span>
            <span className="text-red-700">{cfg.counts.FAILED} failed</span>
            <span>{cfg.counts.QUEUED} queued</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Filter">
          <select className={`${selectCls} w-40`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="notices-filter">
            <option value="">All statuses</option>
            {["QUEUED", "SENT", "SIMULATED", "FAILED"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <button className={btnGhost} onClick={load} data-testid="notices-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        <button className={btnGhost} onClick={sweep} data-testid="notices-sweep-btn"><BellRing className="w-3.5 h-3.5" /> Queue 48h Hearing Reminders Now</button>
        <div className="flex items-end gap-1 border-l border-slate-200 pl-3">
          <Field label="Send a test notice"><input className={`${inputCls} w-56`} value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="notices-test-email" /></Field>
          <button className={btnPrimary} disabled={!testEmail.includes("@")} onClick={test} data-testid="notices-test-btn"><Send className="w-3.5 h-3.5" /> Test</button>
        </div>
      </div>

      <Panel title="Notice log — hearing notices/reminders and filing decisions are queued automatically" testId="notices-panel">
        <div className="max-h-[460px] overflow-y-auto">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Queued</th><th className={thCls}>Type</th><th className={thCls}>Case</th><th className={thCls}>Recipient</th><th className={thCls}>Subject</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id} className="hover:bg-slate-50" data-testid={`notice-row-${n.id}`}>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(n.created_at)}</td>
                  <td className={tdCls}><Badge value={n.notice_type} /></td>
                  <td className={`${tdCls} font-mono text-xs`}>{n.case_number || "—"}</td>
                  <td className={tdCls}>
                    {n.recipient_name}<div className="text-[10px] text-slate-400">{n.recipient_role} · {n.recipient_email}</div>
                  </td>
                  <td className={`${tdCls} text-xs max-w-xs truncate`}>{n.subject}</td>
                  <td className={tdCls}>
                    <Badge value={n.status} testId={`notice-status-${n.id}`} />
                    {n.error && <div className="text-[10px] text-red-600 mt-0.5 max-w-[180px] truncate">{n.error}</div>}
                  </td>
                  <td className={tdCls}>
                    <div className="flex gap-1">
                      {(n.status === "FAILED" || n.status === "QUEUED") && (
                        <button className={btnGhost} onClick={() => resend(n)} aria-label={`Resend notice to ${n.recipient_name}`} data-testid={`notice-resend-${n.id}`}>Resend</button>
                      )}
                      <button className={btnGhost} title="Open a prefilled Gmail compose window"
                        onClick={() => openGmailCompose({ to: n.recipient_email, subject: n.subject, body: (n.body_html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() })}
                        aria-label={`Open in Gmail to ${n.recipient_email}`} data-testid={`notice-gmail-${n.id}`}>
                        <Mail className="w-3.5 h-3.5" /> Gmail
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td className={`${tdCls} text-slate-400`} colSpan={7}>No notices yet — schedule a hearing or decide a filing to generate one.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
